import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { booksData } from '@/pages/ebook/data';

export interface SearchResult {
  id: string;
  type: 'course' | 'book' | 'webinar' | 'mentorship';
  title: string;
  subtitle?: string;
  cover: string;
  price?: number;
  href: string;
  badge?: string;
}

const STATIC_COURSES: SearchResult[] = [
  { id: 'c1', type: 'course', title: 'Formação Completa em Gestão Educacional', subtitle: 'Gestão Escolar · Avançado', cover: '/images/sergio-barros.jpg', price: 997, href: '/curso', badge: 'Mais Vendido' },
  { id: 'c2', type: 'course', title: 'Método de Relações Familiares Eficazes', subtitle: 'Família · Intermediário', cover: '/images/sergio-barros.jpg', price: 797, href: '/curso' },
  { id: 'c3', type: 'course', title: 'Liderança Escolar Estratégica', subtitle: 'Gestão Escolar · Avançado', cover: '/images/sergio-barros.jpg', price: 697, href: '/curso' },
  { id: 'c4', type: 'course', title: 'Inteligência Emocional para Pais', subtitle: 'Família · Iniciante', cover: '/images/sergio-barros.jpg', price: 397, href: '/curso', badge: 'Novo' },
  { id: 'c5', type: 'course', title: 'Comunicação Não-Violenta na Educação', subtitle: 'Desenvolvimento Pessoal · Intermediário', cover: '/images/sergio-barros.jpg', price: 497, href: '/curso' },
  { id: 'c6', type: 'course', title: 'Gestão Financeira para Instituições de Ensino', subtitle: 'Gestão Escolar · Avançado', cover: '/images/sergio-barros.jpg', price: 897, href: '/curso' },
];

const STATIC_BOOKS: SearchResult[] = booksData.map(b => ({
  id: `book-${b.id}`,
  type: 'book' as const,
  title: b.title,
  subtitle: `${b.pages} págs · ${b.format}`,
  cover: b.cover,
  price: b.price,
  href: `/ebook/${b.slug}`,
}));

const STATIC_MENTORSHIP: SearchResult[] = [
  { id: 'silex', type: 'mentorship', title: 'Mentoria SÍLEX', subtitle: 'Método completo em 48 etapas · A partir de R$ 997', cover: '/images/sergio-barros.jpg', href: '/mentoria' },
];

// Busca insensível a acento: "silex" encontra "SÍLEX", "lideranca" encontra "Liderança".
const stripAccents = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function useGlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(async (q: string) => {
    const trimmed = q.trim().toLowerCase();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);

    // Static search (instant)
    const needle = stripAccents(trimmed) || trimmed;
    const staticMatches = [
      ...STATIC_COURSES,
      ...STATIC_BOOKS,
      ...STATIC_MENTORSHIP,
    ].filter(r =>
      stripAccents(r.title).includes(needle) ||
      stripAccents(r.subtitle ?? '').includes(needle)
    );

    // Supabase webinars search
    let webinarResults: SearchResult[] = [];
    try {
      const { data } = await supabase
        .from('webinars')
        .select('id, title, description, cover_url, scheduled_at')
        .ilike('title', `%${trimmed}%`)
        .limit(5);

      webinarResults = (data ?? []).map(w => ({
        id: `webinar-${w.id}`,
        type: 'webinar' as const,
        title: w.title,
        subtitle: w.scheduled_at
          ? new Date(w.scheduled_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
          : 'Em breve',
        cover: w.cover_url ?? '/images/webinar-thumb-default.jpg',
        href: `/webinars/${w.id}`,
        badge: 'Webinar',
      }));
    } catch {
      // ignore supabase errors in search
    }

    setResults([...staticMatches, ...webinarResults].slice(0, 12));
    setLoading(false);
  }, []);

  const handleQueryChange = useCallback((q: string) => {
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(q), 280);
  }, [search]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
  }, []);

  return { query, results, loading, handleQueryChange, clearSearch };
}
